import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import GroupData from "../models/GroupData";
import { fetchFeaturedGroups } from "../services/GroupService";
import { getCachedFeaturedGroups } from "../utils/homePageCache";
import { IMAGE_BASE } from "../Config";
import { getGroupImageUrl, handleImageError } from "../utils/imageUtils";

interface HomeGroupCardProps {
  groups?: GroupData[];
  useCached?: boolean;
}

function HomeGroupCard({ groups, useCached = false }: HomeGroupCardProps = {}) {
  const navigate = useNavigate();
  const [cardData, setCardData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        if (groups) {
          // Use provided groups from props
          setCardData(
            groups.map((group: GroupData) => ({
              imageSrc: group.groupImage,
              title: group.name,
              text: group.description || "Passionate theater group bringing stories to life",
              id: group._id,
              isFeatured: group.isFeatured || false,
            }))
          );
        } else {
          // Fetch groups if not provided
          let response;
          if (useCached) {
            response = await getCachedFeaturedGroups(1, 6);
          } else {
            response = await fetchFeaturedGroups(1, 6);
            // Store in cache for future use
            const { cacheSet } = await import('../utils/cacheUtil');
            await cacheSet(`featured_groups_1_6`, response, 21600); // 6 hours TTL
          }
          setCardData(
            response.groups.map((group: GroupData) => ({
              imageSrc: group.groupImage,
              title: group.name,
              text: group.description || "Passionate theater group bringing stories to life",
              id: group._id,
              isFeatured: group.isFeatured || false,
            }))
          );
        }
      } catch (error) {
        setCardData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [groups]);

  const handleCardClick = (id: string) => {
    navigate(`/group/${id}`);
  };

  if (loading) {
    return (
      <Row className="g-4">
        {[...Array(3)].map((_, index) => (
          <Col key={index} lg={4} md={6}>
            <Card className="h-100 shadow-sm">
              <div className="bg-light" style={{ height: "200px" }}></div>
              <Card.Body>
                <div className="bg-light rounded" style={{ height: "20px", marginBottom: "10px" }}></div>
                <div className="bg-light rounded" style={{ height: "60px" }}></div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Row className="g-4">
      {cardData.length === 0 ? (
        <Col xs={12} className="text-center py-5">
          <i className="bi bi-people display-1 text-muted mb-3"></i>
          <h5 className="text-muted">No theater groups available</h5>
        </Col>
      ) : (
        cardData.map((card) => (
          <Col key={card.id} lg={4} md={6}>
            <Card 
              className="h-100 shadow-sm border-0" 
              onClick={() => handleCardClick(card.id)}
              style={{ cursor: "pointer", transition: "transform 0.2s" }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div className="position-relative">
                <Card.Img 
                  variant="top" 
                  src={getGroupImageUrl(card.imageSrc)} 
                  style={{ height: "200px", objectFit: "cover" }}
                  onError={(e) => handleImageError(e, 'GROUP')}
                />
                {card.isFeatured && (
                  <Badge bg="warning" className="position-absolute top-0 end-0 m-2">
                    Featured
                  </Badge>
                )}
              </div>
              <Card.Body className="d-flex flex-column">
                <Card.Title className="fw-bold">{card.title}</Card.Title>
                <Card.Text className="text-muted flex-grow-1">
                  {card.text.length > 80 ? `${card.text.substring(0, 80)}...` : card.text}
                </Card.Text>
                <small className="text-warning fw-bold mt-auto">
                  View Details <i className="bi bi-arrow-right"></i>
                </small>
              </Card.Body>
            </Card>
          </Col>
        ))
      )}
    </Row>
  );
}

export default HomeGroupCard;
